import React, { useEffect, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import AddNewIcon from 'assets/icons/add-new'
import Accordion from 'components/Accordion'
import { level1Threats } from 'constants/levelOneThreats'
import { level2Threats } from 'constants/levelTwoThreats'
import { level3Threats } from 'constants/levelThreeThreats'
import { siteThreatsTiming } from 'constants/siteThreatsTiming'
import Modal from 'components/Modal'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_EDIT_ONLY_ID, SITE_STATUS_SUBMITTED_EDIT_ONLY_ID } from 'constants/siteStatuses'

import ThreatsHeader from './ThreatsHeader'
import { AddNewThreatsButton } from '../style'
import ThreatsContent from './ThreatsContent'
import ThreatsContentHeader from './ThreatsContentHeader'
import {
  requestCreateSiteThreat,
  requestDeleteSiteThreat,
  requestGetSiteThreats,
  requestUpdateSiteThreat
} from 'api/siteThreat'
import RemoveThreatModal from './RemoveThreatModal'


export const formattedThreatsLevelOne = level1Threats.map((item) => {
  return {
    value: item.id,
    label: item.name
  }
})

export const formattedThreatsLevelTwo = level2Threats.map((item) => {
  return {
    value: item.id,
    label: item.name,
    Level1ThreatId: item.Level1ThreatId
  }
})

export const formattedThreatsLevelThree = level3Threats.map((item) => {
  return {
    value: item.id,
    label: item.name,
    Level1ThreatId: item.Level1ThreatId,
    Level2ThreatId: item.Level2ThreatId
  }
})

export const formattedTimingOptions = siteThreatsTiming.map((item) => {
  return {
    value: item.id,
    label: item.name
  }
})

export const defaultThreatsOptions = {
  level1Threats: formattedThreatsLevelOne,
  level2Threats: formattedThreatsLevelTwo,
  level3Threats: formattedThreatsLevelThree,
  siteThreatsTiming: formattedTimingOptions
}

const defaultThreat = {
  name: 'default',
  Level1ThreatId: null,
  Level2ThreatId: null,
  Level3ThreatId: null,
  siteThreatsTiming: null,
  id: ''
}

const filterAvailableThreatsOptions = (threatsList) => {
  const lvl3 = defaultThreatsOptions.level3Threats.filter((element) => {
    return !threatsList.find((selected) => {
      return selected.Level3ThreatId === element.value
    })
  })

  const lvl2 = defaultThreatsOptions.level2Threats.filter((element) => {
    return (
      lvl3.some((lvl3Threads) => {
        return lvl3Threads.Level2ThreatId === element.value
      }) ||
      !threatsList.find((selected) => {
        return selected.Level2ThreatId === element.value
      })
    )
  })

  const lvl1 = defaultThreatsOptions.level1Threats.filter((element) => {
    return (
      lvl2.some((lvl2Threads) => {
        return lvl2Threads.Level1ThreatId === element.value
      }) ||
      !threatsList.find((selected) => {
        return selected.Level1ThreatId === element.value
      })
    )
  })

  return { level1Threats: lvl1, level2Threats: lvl2, level3Threats: lvl3 }
}

const Threats = ({ site, disabled }) => {
  const [threatsList, setThreatsList] = useState([])
  const options = filterAvailableThreatsOptions(threatsList)
  const [threatForDelete, setThreatForDelete] = useState({})
  const params = useParams()

  const editOnlySiteStatus = site.SiteStatusId === SITE_STATUS_EDIT_ONLY_ID || site.SiteStatusId === SITE_STATUS_SUBMITTED_EDIT_ONLY_ID
  const historyId = editOnlySiteStatus ? site.sitehistoryId : params.siteHistoryId
  const siteId = editOnlySiteStatus ? site.siteId : site.id

  const getThreats = () => {
    requestGetSiteThreats(siteId, historyId).then((data) => {
      const sorted = data.sort((a, b) => {
        return Date.parse(a.createdAt) - Date.parse(b.createdAt)
      })
      setThreatsList(sorted)
    })
  }

  const updateThreats = (threatId, param) => {
    if (!param || !threatId) {
      return
    }

    requestUpdateSiteThreat(threatId, params.siteHistoryId, param).then(() => {
      getThreats()
    })
  }

  const createThreats = (newThreats) => {
    requestCreateSiteThreat(site.id, params.siteHistoryId, newThreats).then((data) => {
      getThreats()
    })
  }

  const handleOpenRemoveModal = (threat) => {
    if (threat.id === '') {
      return getThreats()
    }
    setThreatForDelete(threat)
  }

  const deleteThreats = (id) => {
    if (id === '') {
      return
    }
    requestDeleteSiteThreat(id, params.siteHistoryId).then((data) => {
      getThreats()
    })
  }

  const handleAddThreats = () => {
    const availableToAddNewThreats = threatsList.every((element) => {
      return element.id !== ''
    })

    if (!availableToAddNewThreats) {
      return
    }

    setThreatsList((s) => {
      return [...s, defaultThreat]
    })
  }

  useEffect(() => {
    getThreats()
  }, [])

  return (
    <Container>
      <Row>
        <Col md={8} mdOffset={2}>
          <ThreatsHeader
            title='chooseThreats'
            marginBottom='64px'
            subTitle='whatThreatsAreYouAssessingForThisSite'
            subTitleHint='threatsHint'
          />
        </Col>
      </Row>
      <Row>
        <Col md={8} mdOffset={2}>
          {/* eslint-disable-next-line no-magic-numbers */}
          {threatsList.length === 0 ? (
            <ThreatsContent
              item={defaultThreat}
              options={options}
              createThreats={createThreats}
              defaultThreatsOptions={defaultThreatsOptions}
              updateThreats={updateThreats}
              site={site}
              disabled={disabled}
            />
          ) : (
            <Accordion
              contentComponent={ThreatsContent}
              headerComponent={ThreatsContentHeader}
              headerProps={{ handleOpenRemoveModal, disabled }}
              contentProps={{ options, createThreats, updateThreats, defaultThreatsOptions, site, disabled }}
              items={threatsList}
            />
          )}
        </Col>
      </Row>
      {!(site.SiteStatusId !== SITE_STATUS_DRAFT_ID) && (
        <Row>
          <Col md={8} mdOffset={2}>
            <AddNewThreatsButton onClick={handleAddThreats}
              disabled={disabled}>
              <AddNewIcon />
              <FormattedMessage id='newThreat' />
            </AddNewThreatsButton>
          </Col>
        </Row>
      )}
      <Modal
        width={'416px'}
        isOpen={Boolean(threatForDelete.id)}
        toggleModal={() => {
          setThreatForDelete({})
        }}
        modalTitleId='removeThreat'
        bottomBorder={true}
      >
        <RemoveThreatModal
          item={threatForDelete}
          closeModal={() => {
            setThreatForDelete({})
          }}
          confirmRemove={() => {
            deleteThreats(threatForDelete.id)
            setThreatForDelete({})
          }}
        />
      </Modal>
    </Container>
  )
}

export default Threats
